import { MouseEvent } from 'react';

import { RiExternalLinkLine, RiFileLine } from '@remixicon/react';

import { useExternalDocumentRef } from '@spotify/confidence-external-documents-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  createArtifactExtension,
  isExternalDocument,
  isSafeUrl,
} from '@spotify/plugin-confidence-artifacts-react';
import {
  DefaultArtifactCard,
  ExternalUrlArtifactContent,
  getGradientStartColor,
  getResourceName,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';

const stopPropagation = (e: MouseEvent) => e.stopPropagation();

const getSafeUrl = (url: string | null | undefined): string | undefined =>
  url && isSafeUrl(url) ? url : undefined;

const ExternalDocumentCard = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);

  const handleClick = () => {
    if (safeSourceUrl) {
      window.open(safeSourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <DefaultArtifactCard
      artifact={artifact}
      resolvedDisplayName={externalDocument?.title ?? undefined}
      onClick={handleClick}
      action={
        safeSourceUrl ? (
          <a
            href={safeSourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopPropagation}
            aria-label="Open document"
          >
            <RiExternalLinkLine size={16} />
          </a>
        ) : undefined
      }
    />
  );
};

const ExternalDocumentContent = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);

  if (!safeSourceUrl) {
    return null;
  }

  return (
    <ExternalUrlArtifactContent
      artifact={{ ...artifact, uri: safeSourceUrl }}
      fallbackTitle={() => externalDocument?.title ?? 'Untitled document'}
    />
  );
};

export const ExternalDocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'external-document',
    predicate: artifact => isExternalDocument(artifact.uri),
    icon: () => ({
      Icon: RiFileLine,
      color: getGradientStartColor('blue'),
    }),
    label: 'Document',
    category: 'Documents',
    render: artifact => <ExternalDocumentContent artifact={artifact} />,
    renderCard: artifact => <ExternalDocumentCard artifact={artifact} />,
  }),
);
